
import { Vue, Component } from 'vue-property-decorator';
import { BModal } from 'bootstrap-vue';
import StateButton from '@/components/inputs/StateButton.vue';
import programx from '@/store/modules/programx';
import progressToastx from '@/store/modules/progressToastx';
import { ProgramData, Program, Query, EditState, UpdateProgramSubmission } from '@/store/models.def';

type DifferenceType = 'update' | 'add' | 'delete';

interface DifferenceItem {
	name: string;
	selected: boolean;
	oldQuery: string;
	newQuery: string;
	type: DifferenceType;  // 'update' = both have it, 'add' = only copy has it, 'delete' = only match has it
}

interface MatchedProgram {
	programData: ProgramData;
	matchedQueries: string[];  // Query file names that match
	isFullMatch: boolean;
	selected: boolean;
	displayName: string;
	isProgramToMatch: boolean;
}

interface UnmatchedProgram {
	programData: ProgramData;
	displayName: string;
}

// Helper to get all queries from a program as a map: file -> query object
function getQueryMap(program: ProgramData): Map<string, Query> {
	const map = new Map<string, Query>();
	for (const group of program.groups) {
		for (const query of group.queries) {
			if (query.file) {
				map.set(query.file, query);
			}
		}
	}
	return map;
}

// Compare two queries by their content (query string), not the entire object
function queriesEqual(q1: Query, q2: Query): boolean {
	return q1.query === q2.query;
}

@Component({
	components: {
		StateButton,
	},
})
export default class BatchUpdateModal extends Vue {
	// Selection state
	public programToMatch: ProgramData | null = null;
	public programToCopy: ProgramData | null = null;

	// Preview state
	public previewGenerated: boolean = false;
	public differences: DifferenceItem[] = [];  // Query file names that differ
	public matchedPrograms: MatchedProgram[] = [];
	public unmatchedPrograms: UnmatchedProgram[] = [];

	// UI state
	public copyState: EditState = 'active';

	// Computed properties
	public get allPrograms(): ProgramData[] {
		return programx.allPrograms;
	}

	public get programOptions() {
		return this.allPrograms.map((p) => ({
			value: p,
			text: `${p.id} (${p.softwareType})`,
		}));
	}

	public get canPreview(): boolean {
		return this.programToMatch !== null &&
			this.programToCopy !== null &&
			this.programToMatch.id !== this.programToCopy.id;
	}

	public get selectedCount(): number {
		return this.matchedPrograms.filter((m) => m.selected).length;
	}

	public get selectedDifferencesCount(): number {
		return this.differences.filter((d) => d.selected).length;
	}

	public get selectedDifferenceNames(): string[] {
		return this.differences.filter((d) => d.selected).map((d) => d.name);
	}

	public get copyButtonLabel(): string {
		const labels: Record<EditState, string> = {
			active: `Copy to ${this.selectedCount} Program(s)`,
			sending: 'Copying...',
			done: 'Copied!',
		};
		return labels[this.copyState];
	}

	// Methods
	public show(): void {
		const modal = this.$refs.batchUpdateModal as BModal;
		if (modal) {
			modal.show();
		}
	}

	public hide(): void {
		const modal = this.$refs.batchUpdateModal as BModal;
		if (modal) {
			modal.hide();
		}
	}

	public resetModal(): void {
		this.programToMatch = null;
		this.programToCopy = null;
		this.resetPreview();
	}

	public resetPreview(): void {
		this.previewGenerated = false;
		this.differences = [];
		this.matchedPrograms = [];
		this.unmatchedPrograms = [];
	}

	public generatePreview(): void {
		if (!this.programToMatch || !this.programToCopy) { return; }

		// Find differences at query level
		this.differences = this._findDifferences(this.programToMatch, this.programToCopy);

		// Find matching programs based on selected differences
		this.updateMatchedPrograms();
		this.previewGenerated = true;
	}

	public updateMatchedPrograms(): void {
		if (!this.programToMatch || !this.programToCopy) { return; }

		const selectedDiffs = this.differences.filter((d) => d.selected);

		if (selectedDiffs.length === 0) {
			this.matchedPrograms = [];
			this.unmatchedPrograms = [];
			return;
		}

		const result = this._findMatchingPrograms(
			this.programToMatch,
			this.programToCopy,
			selectedDiffs,
			this.allPrograms,
		);

		this.matchedPrograms = result.matched;
		this.unmatchedPrograms = result.unmatched;
	}

	public selectAll(): void {
		this.matchedPrograms.forEach((m) => {
			m.selected = true;
		});
	}

	public unselectAll(): void {
		this.matchedPrograms.forEach((m) => {
			m.selected = false;
		});
	}

	public selectAllDifferences(): void {
		this.differences.forEach((d) => (d.selected = true));
		this.updateMatchedPrograms();
	}

	public unselectAllDifferences(): void {
		this.differences.forEach((d) => (d.selected = false));
		this.updateMatchedPrograms();
	}

	public toggleDifference(diff: DifferenceItem): void {
		diff.selected = !diff.selected;
		this.updateMatchedPrograms();
	}

	public getBadgeClass(diff: DifferenceItem): string {
		if (diff.selected) {
			switch (diff.type) {
				case 'add':
					return 'badge-success border-success';  // Green - add new query
				case 'delete':
					return 'badge-pink border-pink';  // Pink - delete query
				case 'update':
				default:
					return 'badge-warning border-warning';  // Yellow - update existing
			}
		} else {
			// Unselected: transparent background but colored border
			switch (diff.type) {
				case 'add':
					return 'bg-transparent text-success border-success';
				case 'delete':
					return 'bg-transparent text-pink border-pink';
				case 'update':
				default:
					return 'bg-transparent text-warning border-warning';
			}
		}
	}

	public getDiffTooltip(diff: DifferenceItem): string {
		const oldLines = diff.oldQuery ? diff.oldQuery.split('\n') : [];
		const newLines = diff.newQuery.split('\n');

		let html = '<div class="text-left" style="font-family: monospace; font-size: 11px; max-height: 300px; overflow-y: auto; white-space: pre;">';

		if (!diff.oldQuery) {
			// New query (doesn't exist in program-to-match)
			html += '<div class="text-success">+ (new query)</div>';
			newLines.forEach((line) => {
				html += `<div class="text-success">+ ${this.escapeHtml(line)}</div>`;
			});
		} else {
			// Show simple diff
			oldLines.forEach((line) => {
				if (!newLines.includes(line)) {
					html += `<div class="text-danger">- ${this.escapeHtml(line)}</div>`;
				}
			});
			newLines.forEach((line) => {
				if (!oldLines.includes(line)) {
					html += `<div class="text-success">+ ${this.escapeHtml(line)}</div>`;
				}
			});
		}

		html += '</div>';
		return html;
	}

	public escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');
	}

	public async applyBatchUpdate(): Promise<void> {
		if (this.copyState !== 'active' || !this.programToCopy) { return; }

		const selectedPrograms = this.matchedPrograms.filter((m) => m.selected);
		const selectedDiffs = this.differences.filter((d) => d.selected);
		const copyQueryMap = getQueryMap(this.programToCopy);

		// Build a map of query file -> group name in programToCopy (for add operations)
		const copyQueryGroupMap = new Map<string, string>();
		for (const group of this.programToCopy.groups) {
			for (const query of group.queries) {
				if (query.file && group.group) {
					copyQueryGroupMap.set(query.file, group.group);
				}
			}
		}

		const toastId = await progressToastx.add({
			title: 'Batch Update',
			state: `Updating ${selectedPrograms.length} programs...`,
			variant: 'info',
		});

		this.copyState = 'sending';
		let successCount = 0;
		let failCount = 0;

		for (const matched of selectedPrograms) {
			// Deep clone the groups
			const updatedGroups: Program[] = JSON.parse(JSON.stringify(matched.programData.groups));

			// Process each selected difference that matches this program
			for (const diff of selectedDiffs) {
				if (!matched.matchedQueries.includes(diff.name)) {
					continue;
				}

				if (diff.type === 'update') {
					// UPDATE: Replace the query
					for (const group of updatedGroups) {
						for (let i = 0; i < group.queries.length; i++) {
							if (group.queries[i].file === diff.name) {
								const copyQuery = copyQueryMap.get(diff.name);
								if (copyQuery) {
									group.queries[i] = JSON.parse(JSON.stringify(copyQuery));
								}
							}
						}
					}
				} else if (diff.type === 'add') {
					// ADD: Add the query to the appropriate group
					const copyQuery = copyQueryMap.get(diff.name);
					const targetGroupName = copyQueryGroupMap.get(diff.name);
					if (copyQuery && targetGroupName) {
						let targetGroup = updatedGroups.find((g) => g.group === targetGroupName);
						if (!targetGroup) {
							// Create the group if it doesn't exist
							targetGroup = { group: targetGroupName, target: 'path', queries: [] };
							updatedGroups.push(targetGroup);
						}
						targetGroup.queries.push(JSON.parse(JSON.stringify(copyQuery)));
					}
				} else if (diff.type === 'delete') {
					// DELETE: Remove the query
					for (const group of updatedGroups) {
						group.queries = group.queries.filter((q) => q.file !== diff.name);
					}
				}
			}

			// Remove empty groups (groups with no queries left after deletions)
			const filteredGroups = updatedGroups.filter((g) => g.queries.length > 0);

			const submission: UpdateProgramSubmission = {
				id: matched.programData.id,
				groups: filteredGroups,
			};

			console.log(`Updating ${submission.id}...`);
			let result = await programx.updateProgram(submission);
			// Handle case where result comes back as array
			if (Array.isArray(result)) {
				result = result[0];
			}
			console.log(`Result for ${submission.id}:`, result);

			if (result && result.success) {
				successCount++;
			} else if (result && result.errorCode === 'not-changed') {
				// No actual changes needed, count as success
				successCount++;
			} else {
				failCount++;
				console.error(`Failed to update ${matched.programData.id}:`, result);
			}
		}

		this.copyState = 'active';

		if (failCount === 0) {
			progressToastx.done({
				id: toastId,
				state: `Successfully updated ${successCount} programs!`,
			});
		} else {
			progressToastx.error({
				id: toastId,
				state: `Updated ${successCount} programs, ${failCount} failed.`,
			});
		}

		this.hide();
	}

	private _findDifferences(programToMatch: ProgramData, programToCopy: ProgramData): DifferenceItem[] {
		const differences: DifferenceItem[] = [];
		const matchQueryMap = getQueryMap(programToMatch);
		const copyQueryMap = getQueryMap(programToCopy);

		// Find queries in copyProgram that differ from or don't exist in matchProgram
		for (const [fileName, copyQuery] of copyQueryMap) {
			const matchQuery = matchQueryMap.get(fileName);
			if (!matchQuery) {
				// ADD: exists in copy but not in match (green)
				differences.push({
					name: fileName,
					selected: false,  // Not selected by default
					oldQuery: '',
					newQuery: copyQuery.query,
					type: 'add',
				});
			} else if (!queriesEqual(matchQuery, copyQuery)) {
				// UPDATE: exists in both but different (yellow)
				differences.push({
					name: fileName,
					selected: true,
					oldQuery: matchQuery.query,
					newQuery: copyQuery.query,
					type: 'update',
				});
			}
		}

		// Find queries in matchProgram that don't exist in copyProgram (DELETE - pink)
		for (const [fileName, matchQuery] of matchQueryMap) {
			const copyQuery = copyQueryMap.get(fileName);
			if (!copyQuery) {
				differences.push({
					name: fileName,
					selected: false,  // Not selected by default
					oldQuery: matchQuery.query,
					newQuery: '',
					type: 'delete',
				});
			}
		}

		return differences;
	}

	private _findMatchingPrograms(
		programToMatch: ProgramData,
		programToCopy: ProgramData,
		differences: DifferenceItem[],
		allPrograms: ProgramData[],
	): { matched: MatchedProgram[]; unmatched: UnmatchedProgram[] } {
		const matched: MatchedProgram[] = [];
		const unmatched: UnmatchedProgram[] = [];
		const matchQueryMap = getQueryMap(programToMatch);

		for (const program of allPrograms) {
			// Skip program-to-copy
			if (program.id === programToCopy.id) {
				continue;
			}

			const programQueryMap = getQueryMap(program);
			const matchedQueries: string[] = [];
			const unmatchedQueries: string[] = [];

			for (const diff of differences) {
				const matchQuery = matchQueryMap.get(diff.name);
				const programQuery = programQueryMap.get(diff.name);

				if (diff.type === 'add') {
					// For 'add' type: match if program does NOT have the query
					if (!programQuery) {
						matchedQueries.push(diff.name);
					} else {
						unmatchedQueries.push(diff.name);
					}
				} else {
					// For 'update' and 'delete' types: match if program HAS the query and it equals match
					if (programQuery) {
						if (matchQuery && queriesEqual(matchQuery, programQuery)) {
							matchedQueries.push(diff.name);
						} else {
							unmatchedQueries.push(diff.name);
						}
					} else {
						unmatchedQueries.push(diff.name);
					}
				}
			}

			const isProgramToMatch = program.id === programToMatch.id;

			// Check if program has same prefix (e.g., "sql-" or "autocount-")
			const programPrefix = program.id.split('-')[0];
			const matchPrefix = programToMatch.id.split('-')[0];
			const hasSamePrefix = programPrefix === matchPrefix;

			if (matchedQueries.length > 0) {
				const isFullMatch = matchedQueries.length === differences.length &&
					unmatchedQueries.length === 0;
				matched.push({
					programData: program,
					matchedQueries,
					isFullMatch,
					selected: hasSamePrefix,  // Only auto-select if same prefix
					displayName: isFullMatch
						? program.id
						: `${program.id} (${matchedQueries.join(', ')})`,
					isProgramToMatch,
				});
			} else {
				// Program has no matching queries (either unmatched or doesn't have the query at all)
				unmatched.push({
					programData: program,
					displayName: unmatchedQueries.length > 0 && unmatchedQueries.length < differences.length
						? `${program.id} (${unmatchedQueries.join(', ')})`
						: program.id,
				});
			}
		}

		// Sort matched: reference program first, then full matches, then partial
		matched.sort((a, b) => {
			if (a.isProgramToMatch) { return -1; }
			if (b.isProgramToMatch) { return 1; }
			if (a.isFullMatch && !b.isFullMatch) { return -1; }
			if (!a.isFullMatch && b.isFullMatch) { return 1; }
			return a.programData.id.localeCompare(b.programData.id);
		});

		return { matched, unmatched };
	}
}
